import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
	CAN_REDO_COMMAND,
	CAN_UNDO_COMMAND,
	REDO_COMMAND,
	UNDO_COMMAND,
	SELECTION_CHANGE_COMMAND,
	FORMAT_TEXT_COMMAND,
	FORMAT_ELEMENT_COMMAND,
	$getSelection,
	$isRangeSelection,
	$createParagraphNode,
	$getNodeByKey,
	LexicalEditor,
	RangeSelection,
	LexicalNode
} from "lexical";
import { $isLinkNode, TOGGLE_LINK_COMMAND } from "@lexical/link";
import {
	$isParentElementRTL,
	$wrapNodes,
	$isAtNodeEnd
} from "@lexical/selection";
import { $getNearestNodeOfType, mergeRegister } from "@lexical/utils";
import {
	INSERT_ORDERED_LIST_COMMAND,
	INSERT_UNORDERED_LIST_COMMAND,
	REMOVE_LIST_COMMAND,
	$isListNode,
	ListNode
} from "@lexical/list";
import { createPortal } from "react-dom";
import {
	$createHeadingNode,
	$createQuoteNode,
	$isHeadingNode
} from "@lexical/rich-text";
import {
	$createCodeNode,
	$isCodeNode,
	getDefaultCodeLanguage,
	getCodeLanguages
} from "@lexical/code";

const LowPriority = 1;

type BlockType = 
	| "paragraph"
	| "quote"
	| "code"
	| "h1"
	| "h2"
	| "h3"
	| "h4"
	| "h5"
	| "ul"
	| "ol";

// Include all block types to ensure they're always supported
const supportedBlockTypes = new Set<BlockType>([
	"paragraph",
	"quote",
	"code",
	"h1",
	"h2",
	"h3",
	"h4",
	"h5",
	"ul",
	"ol"
]);

const blockTypeToBlockName: Record<BlockType, string> = {
	code: "Code Block",
	h1: "Large Heading",
	h2: "Small Heading",
	h3: "Heading",
	h4: "Heading",
	h5: "Heading",
	ol: "Numbered List",
	paragraph: "Normal",
	quote: "Quote",
	ul: "Bulleted List"
};

function Divider(): JSX.Element {
	return <div className="divider" />;
}

function positionEditorElement(editor: HTMLElement, rect: DOMRect | null): void {
	if (rect === null) {
		editor.style.opacity = "0";
		editor.style.top = "-1000px";
		editor.style.left = "-1000px";
	} else {
		editor.style.opacity = "1";
		editor.style.top = `${rect.top + rect.height + window.pageYOffset + 10}px`;
		editor.style.left = `${
			rect.left + window.pageXOffset - editor.offsetWidth / 2 + rect.width / 2
		}px`;
	}
}

interface FloatingLinkEditorProps {
	editor: LexicalEditor;
}

function FloatingLinkEditor({ editor }: FloatingLinkEditorProps): JSX.Element {
	const editorRef = useRef<HTMLDivElement>(null);
	const inputRef = useRef<HTMLInputElement>(null);
	const mouseDownRef = useRef(false);
	const [linkUrl, setLinkUrl] = useState("");
	const [isEditMode, setEditMode] = useState(false);
	const [lastSelection, setLastSelection] = useState<RangeSelection | null>(null);

	const updateLinkEditor = useCallback(() => {
		const selection = $getSelection();
		if ($isRangeSelection(selection)) {
			const node = getSelectedNode(selection);
			const parent = node.getParent();
			if ($isLinkNode(parent)) {
				setLinkUrl(parent.getURL());
			} else if ($isLinkNode(node)) {
				setLinkUrl(node.getURL());
			} else {
				setLinkUrl("");
			}
		}
		const editorElem = editorRef.current;
		const nativeSelection = window.getSelection();
		const activeElement = document.activeElement;

		if (editorElem === null) {
			return;
		}

		const rootElement = editor.getRootElement();
		if (
			selection !== null &&
			nativeSelection !== null &&
			!nativeSelection.isCollapsed &&
			rootElement !== null &&
			rootElement.contains(nativeSelection.anchorNode)
		) {
			const domRange = nativeSelection.getRangeAt(0);
			let rect: DOMRect;
			if (nativeSelection.anchorNode === rootElement) {
				let inner = rootElement;
				while (inner.firstElementChild != null) {
					inner = inner.firstElementChild as HTMLElement;
				}
				rect = inner.getBoundingClientRect();
			} else {
				rect = domRange.getBoundingClientRect();
			}

			if (!mouseDownRef.current) {
				positionEditorElement(editorElem, rect);
			}
			setLastSelection(selection as RangeSelection);
		} else if (!activeElement || activeElement.className !== "link-input") {
			positionEditorElement(editorElem, null);
			setLastSelection(null);
			setEditMode(false);
			setLinkUrl("");
		}

		return true;
	}, [editor]);

	useEffect(() => {
		return mergeRegister(
			editor.registerUpdateListener(({ editorState }) => {
				editorState.read(() => {
					updateLinkEditor();
				});
			}),

			editor.registerCommand(
				SELECTION_CHANGE_COMMAND,
				() => {
					updateLinkEditor();
					return true;
				},
				LowPriority
			)
		);
	}, [editor, updateLinkEditor]);

	useEffect(() => {
		editor.getEditorState().read(() => {
			updateLinkEditor();
		});
	}, [editor, updateLinkEditor]);

	useEffect(() => {
		if (isEditMode && inputRef.current) {
			inputRef.current.focus();
		}
	}, [isEditMode]);

	return (
		<div ref={editorRef} className="link-editor">
			{isEditMode ? (
				<input
					ref={inputRef}
					className="link-input"
					value={linkUrl}
					onChange={(event) => {
						setLinkUrl(event.target.value);
					}}
					onKeyDown={(event) => {
						if (event.key === "Enter") {
							event.preventDefault();
							if (lastSelection !== null) {
								if (linkUrl !== "") {
									editor.dispatchCommand(TOGGLE_LINK_COMMAND, linkUrl);
								}
								setEditMode(false);
							}
						} else if (event.key === "Escape") {
							event.preventDefault();
							setEditMode(false);
						}
					}}
				/>
			) : (
				<>
					<div className="link-input">
						<a href={linkUrl} target="_blank" rel="noopener noreferrer">
							{linkUrl}
						</a>
						<div
							className="link-edit"
							role="button"
							tabIndex={0}
							onMouseDown={(event) => event.preventDefault()}
							onClick={() => {
								setEditMode(true);
							}}
						/>
					</div>
				</>
			)}
		</div>
	);
}

interface SelectProps {
	onChange: (event: React.ChangeEvent<HTMLSelectElement>) => void;
	className: string;
	options: string[];
	value: string;
}

function Select({ onChange, className, options, value }: SelectProps): JSX.Element {
	return (
		<select 
			className={className} 
			onChange={onChange} 
			value={value}
			// Ensure the dropdown is always accessible
			style={{ display: "inline-block" }}
		>
			<option hidden={true} value="" />
			{options.map((option) => (
				<option key={option} value={option}>
					{option}
				</option>
			))}
		</select>
	);
}

function getSelectedNode(selection: RangeSelection): LexicalNode {
	const anchor = selection.anchor;
	const focus = selection.focus;
	const anchorNode = selection.anchor.getNode();
	const focusNode = selection.focus.getNode();
	if (anchorNode === focusNode) {
		return anchorNode;
	}
	const isBackward = selection.isBackward();
	if (isBackward) {
		return $isAtNodeEnd(focus) ? anchorNode : focusNode;
	} else {
		return $isAtNodeEnd(anchor) ? focusNode : anchorNode;
	}
}

interface BlockOptionsDropdownListProps {
	editor: LexicalEditor;
	blockType: string;
	toolbarRef: React.RefObject<HTMLDivElement>;
	setShowBlockOptionsDropDown: (show: boolean) => void;
	setBlockType: (blockType: BlockType) => void; // Add setBlockType to props
}

function BlockOptionsDropdownList({
	editor,
	blockType,
	toolbarRef,
	setShowBlockOptionsDropDown,
	setBlockType
}: BlockOptionsDropdownListProps): JSX.Element {
	const dropDownRef = useRef<HTMLDivElement>(null);

	// Position the dropdown below the toolbar
	useEffect(() => {
		const toolbar = toolbarRef.current;
		const dropDown = dropDownRef.current;

		if (toolbar !== null && dropDown !== null) {
			// Get button's position
			const buttonElem = toolbar.querySelector(".block-controls");
			if (buttonElem) {
				const buttonRect = buttonElem.getBoundingClientRect();
				
				// Position dropdown below the format button
				dropDown.style.top = `${buttonRect.bottom + window.scrollY}px`;
				dropDown.style.left = `${buttonRect.left + window.scrollX}px`;
				
				// Ensure the dropdown is visible
				dropDown.style.position = "absolute";
				dropDown.style.zIndex = "10000";
				dropDown.style.display = "block";
				dropDown.style.backgroundColor = "#fff";
				dropDown.style.boxShadow = "0 4px 8px rgba(0, 0, 0, 0.2)";
				dropDown.style.borderRadius = "4px";
				dropDown.style.minWidth = "200px";
				dropDown.style.border = "1px solid #ccc";
			}
		}
	}, [dropDownRef, toolbarRef]);

	// Handle clicks outside to close the dropdown
	useEffect(() => {
		const dropDown = dropDownRef.current;
		const toolbar = toolbarRef.current;

		if (dropDown !== null && toolbar !== null) {
			const handle = (event: MouseEvent) => {
				const target = event.target as Node;
				
				// Prevent clicks on the dropdown or toolbar from closing it
				if (!dropDown.contains(target) && !toolbar.contains(target)) {
					setShowBlockOptionsDropDown(false);
				}
			};
			
			// Use capture phase to ensure this handler runs first
			document.addEventListener("mousedown", handle, true);

			return () => {
				document.removeEventListener("mousedown", handle, true);
			};
		}
	}, [dropDownRef, setShowBlockOptionsDropDown, toolbarRef]);

	const formatParagraph = () => {
		try {
			// Force focus on editor before applying format
			editor.focus();
			
			editor.update(() => {
				const selection = $getSelection();
				if ($isRangeSelection(selection)) {
					$wrapNodes(selection, () => $createParagraphNode());
					console.log("Paragraph node created and wrapped");
				}
			});
			
			// Update UI after operation
			setBlockType("paragraph");
			console.log("Format changed to paragraph");
		} catch (error) {
			console.error("Error formatting paragraph:", error);
		}
		// Close the dropdown
		setShowBlockOptionsDropDown(false);
	};

	const formatLargeHeading = () => {
		try {
			// Force focus on editor before applying format
			editor.focus();
			
			editor.update(() => {
				const selection = $getSelection();
				if ($isRangeSelection(selection)) {
					$wrapNodes(selection, () => $createHeadingNode("h1"));
					console.log("H1 node created and wrapped");
				}
			});
			
			// Update UI after operation
			setBlockType("h1");
			console.log("Format changed to h1");
		} catch (error) {
			console.error("Error formatting h1:", error);
		}
		// Close the dropdown
		setShowBlockOptionsDropDown(false);
	};

	const formatSmallHeading = () => {
		try {
			// Force focus on editor before applying format
			editor.focus();
			
			editor.update(() => {
				const selection = $getSelection();
				if ($isRangeSelection(selection)) {
					$wrapNodes(selection, () => $createHeadingNode("h2"));
					console.log("H2 node created and wrapped");
				}
			});
			
			// Update UI after operation
			setBlockType("h2");
			console.log("Format changed to h2");
		} catch (error) {
			console.error("Error formatting h2:", error);
		}
		// Close the dropdown
		setShowBlockOptionsDropDown(false);
	};

	
	// Direct list formatting functions for ToolbarPlugin
	// These bypass all the complexity and focus on just making the commands work

	// For bullet lists
	const formatBulletList = () => {
		// Force focus before any operation
		editor.focus();
		
		// Give a small timeout to ensure editor has focus
		setTimeout(() => {
			// Direct command dispatch
			editor.dispatchCommand(INSERT_UNORDERED_LIST_COMMAND, undefined);
			console.log("Bullet list command dispatched via direct method");
			
			// Force UI update
			setBlockType("ul");
			
			// Close dropdown
			setShowBlockOptionsDropDown(false);
		}, 10);
	};

	// For numbered lists
	const formatNumberedList = () => {
		// Force focus before any operation
		editor.focus();
		
		// Give a small timeout to ensure editor has focus
		setTimeout(() => {
			// Direct command dispatch
			editor.dispatchCommand(INSERT_ORDERED_LIST_COMMAND, undefined);
			console.log("Numbered list command dispatched via direct method");
			
			// Force UI update
			setBlockType("ol");
			
			// Close dropdown
			setShowBlockOptionsDropDown(false);
		}, 10);
	};

	const formatQuote = () => {
		try {
			// Force focus on editor before applying format
			editor.focus();
			
			editor.update(() => {
				const selection = $getSelection();
				if ($isRangeSelection(selection)) {
					$wrapNodes(selection, () => $createQuoteNode());
					console.log("Quote node created and wrapped");
				}
			});
			
			// Update UI after operation
			setBlockType("quote");
			console.log("Format changed to quote");
		} catch (error) {
			console.error("Error formatting quote:", error);
		}
		// Close the dropdown
		setShowBlockOptionsDropDown(false);
	};

	const formatCode = () => {
		try {
			// Force focus on editor before applying format
			editor.focus();
			
			editor.update(() => {
				const selection = $getSelection();
				if ($isRangeSelection(selection)) {
					$wrapNodes(selection, () => $createCodeNode());
					console.log("Code node created and wrapped");
				}
			});
			
			// Update UI after operation
			setBlockType("code");
			console.log("Format changed to code block");
		} catch (error) {
			console.error("Error formatting code block:", error);
		}
		// Close the dropdown
		setShowBlockOptionsDropDown(false);
	};

	return (
		<div 
			className="dropdown" 
			ref={dropDownRef}
			style={{ 
				position: "absolute",
				backgroundColor: "#fff",
				border: "1px solid #ccc",
				borderRadius: "4px",
				padding: "8px 0",
				boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)",
				minWidth: "180px",
				zIndex: 10000
			}}
		>
			<button 
				className="item" 
				onMouseDown={(e) => {
					e.preventDefault();
					e.stopPropagation();
					formatParagraph();
				}}
				style={{
					display: "flex",
					alignItems: "center",
					padding: "8px 12px",
					width: "100%",
					textAlign: "left",
					background: blockType === "paragraph" ? "#f0f0f0" : "transparent",
					border: "none",
					cursor: "pointer"
				}}
			>
				<span className="icon paragraph" />
				<span className="text">Normal</span>
				{blockType === "paragraph" && <span className="active" style={{ marginLeft: "auto", color: "blue" }}>✓</span>}
			</button>
			<button 
				className="item" 
				onMouseDown={(e) => {
					e.preventDefault();
					e.stopPropagation();
					formatLargeHeading();
				}}
				style={{
					display: "flex",
					alignItems: "center",
					padding: "8px 12px",
					width: "100%",
					textAlign: "left",
					background: blockType === "h1" ? "#f0f0f0" : "transparent",
					border: "none",
					cursor: "pointer"
				}}
			>
				<span className="icon large-heading" />
				<span className="text">Large Heading</span>
				{blockType === "h1" && <span className="active" style={{ marginLeft: "auto", color: "blue" }}>✓</span>}
			</button>
			<button 
				className="item" 
				onMouseDown={(e) => {
					e.preventDefault();
					e.stopPropagation();
					formatSmallHeading();
				}}
				style={{
					display: "flex",
					alignItems: "center",
					padding: "8px 12px",
					width: "100%",
					textAlign: "left",
					background: blockType === "h2" ? "#f0f0f0" : "transparent",
					border: "none",
					cursor: "pointer"
				}}
			>
				<span className="icon small-heading" />
				<span className="text">Small Heading</span>
				{blockType === "h2" && <span className="active" style={{ marginLeft: "auto", color: "blue" }}>✓</span>}
			</button>
			<button 
				className="item" 
				onMouseDown={(e) => {
					e.preventDefault();
					e.stopPropagation();
					formatBulletList();
				}}
				style={{
					display: "flex",
					alignItems: "center",
					padding: "8px 12px",
					width: "100%",
					textAlign: "left",
					background: blockType === "ul" ? "#f0f0f0" : "transparent",
					border: "none",
					cursor: "pointer"
				}}
			>
				<span className="icon bullet-list" />
				<span className="text">Bullet List</span>
				{blockType === "ul" && <span className="active" style={{ marginLeft: "auto", color: "blue" }}>✓</span>}
			</button>
			<button 
				className="item" 
				onMouseDown={(e) => {
					e.preventDefault();
					e.stopPropagation();
					formatNumberedList();
				}}
				style={{
					display: "flex",
					alignItems: "center",
					padding: "8px 12px",
					width: "100%",
					textAlign: "left",
					background: blockType === "ol" ? "#f0f0f0" : "transparent",
					border: "none",
					cursor: "pointer"
				}}
			>
				<span className="icon numbered-list" />
				<span className="text">Numbered List</span>
				{blockType === "ol" && <span className="active" style={{ marginLeft: "auto", color: "blue" }}>✓</span>}
			</button>
			<button 
				className="item" 
				onMouseDown={(e) => {
					e.preventDefault();
					e.stopPropagation();
					formatQuote();
				}}
				style={{
					display: "flex",
					alignItems: "center",
					padding: "8px 12px",
					width: "100%",
					textAlign: "left",
					background: blockType === "quote" ? "#f0f0f0" : "transparent",
					border: "none",
					cursor: "pointer"
				}}
			>
				<span className="icon quote" />
				<span className="text">Quote</span>
				{blockType === "quote" && <span className="active" style={{ marginLeft: "auto", color: "blue" }}>✓</span>}
			</button>
			<button 
				className="item" 
				onMouseDown={(e) => {
					e.preventDefault();
					e.stopPropagation();
					formatCode();
				}}
				style={{
					display: "flex",
					alignItems: "center",
					padding: "8px 12px",
					width: "100%",
					textAlign: "left",
					background: blockType === "code" ? "#f0f0f0" : "transparent",
					border: "none",
					cursor: "pointer"
				}}
			>
				<span className="icon code" />
				<span className="text">Code Block</span>
				{blockType === "code" && <span className="active" style={{ marginLeft: "auto", color: "blue" }}>✓</span>}
			</button>
		</div>
	);
}

export default function ToolbarPlugin(): JSX.Element {
	const [editor] = useLexicalComposerContext();
	const toolbarRef = useRef<HTMLDivElement>(null);
	const [canUndo, setCanUndo] = useState(false);
	const [canRedo, setCanRedo] = useState(false);
	const [blockType, setBlockType] = useState<BlockType>("paragraph");
	const [selectedElementKey, setSelectedElementKey] = useState<string | null>(null);
	const [showBlockOptionsDropDown, setShowBlockOptionsDropDown] = useState(
		false
	);
	
	// Handle clicks outside the dropdown to close it
	useEffect(() => {
		const handleClickOutside = (event: MouseEvent) => {
			if (
				toolbarRef.current &&
				!toolbarRef.current.contains(event.target as Node) &&
				showBlockOptionsDropDown
			) {
				setShowBlockOptionsDropDown(false);
			}
		};
		
		document.addEventListener("mousedown", handleClickOutside);
		return () => {
			document.removeEventListener("mousedown", handleClickOutside);
		};
	}, [showBlockOptionsDropDown]);
	const [codeLanguage, setCodeLanguage] = useState("");
	const [isRTL, setIsRTL] = useState(false);
	const [isLink, setIsLink] = useState(false);
	const [isBold, setIsBold] = useState(false);
	const [isItalic, setIsItalic] = useState(false);
	const [isUnderline, setIsUnderline] = useState(false);
	const [isStrikethrough, setIsStrikethrough] = useState(false);
	const [isCode, setIsCode] = useState(false);

	const updateToolbar = useCallback(() => {
		const selection = $getSelection();
		if ($isRangeSelection(selection)) {
			const anchorNode = selection.anchor.getNode();
			const element =
				anchorNode.getKey() === "root"
					? anchorNode
					: anchorNode.getTopLevelElementOrThrow();
			const elementKey = element.getKey();
			const elementDOM = editor.getElementByKey(elementKey);
			if (elementDOM !== null) {
				setSelectedElementKey(elementKey);
				if ($isListNode(element)) {
					const parentList = $getNearestNodeOfType(anchorNode, ListNode);
					const type = parentList ? parentList.getTag() : element.getTag();
					// Ensure the type is a valid BlockType
					if (type === "ul" || type === "ol" || 
							type === "paragraph" || type === "h1" || type === "h2" || 
							type === "h3" || type === "h4" || type === "h5" || 
							type === "quote" || type === "code") {
						setBlockType(type as BlockType);
						console.log("Setting block type to:", type);
					} else {
						// Default to paragraph if invalid type
						setBlockType("paragraph");
						console.log("Invalid type, defaulting to paragraph");
					}
				} else {
					const type = $isHeadingNode(element)
						? element.getTag()
						: element.getType();
					
					// Ensure the type is a valid BlockType
					if (type === "ul" || type === "ol" || 
							type === "paragraph" || type === "h1" || type === "h2" || 
							type === "h3" || type === "h4" || type === "h5" || 
							type === "quote" || type === "code") {
						setBlockType(type as BlockType);
						console.log("Setting block type to:", type);
					} else {
						// Default to paragraph if invalid type
						setBlockType("paragraph");
						console.log("Invalid type, defaulting to paragraph");
					}
					
					if ($isCodeNode(element)) {
						setCodeLanguage(element.getLanguage() || getDefaultCodeLanguage());
					}
				}
			} else {
				// If no element is found, default to paragraph
				setBlockType("paragraph");
				console.log("No element found, defaulting to paragraph");
			}
			// Update text format
			setIsBold(selection.hasFormat("bold"));
			setIsItalic(selection.hasFormat("italic"));
			setIsUnderline(selection.hasFormat("underline"));
			setIsStrikethrough(selection.hasFormat("strikethrough"));
			setIsCode(selection.hasFormat("code"));
			setIsRTL($isParentElementRTL(selection));

			// Update links
			const node = getSelectedNode(selection);
			const parent = node.getParent();
			if ($isLinkNode(parent) || $isLinkNode(node)) {
				setIsLink(true);
			} else {
				setIsLink(false);
			}
		}
	}, [editor]);

	useEffect(() => {
		return mergeRegister(
			editor.registerUpdateListener(({ editorState }) => {
				editorState.read(() => {
					updateToolbar();
				});
			}),
			editor.registerCommand(
				SELECTION_CHANGE_COMMAND,
				(_payload, newEditor) => {
					updateToolbar();
					return false;
				},
				LowPriority
			),
			editor.registerCommand(
				CAN_UNDO_COMMAND,
				(payload: boolean) => {
					setCanUndo(payload);
					return false;
				},
				LowPriority
			),
			editor.registerCommand(
				CAN_REDO_COMMAND,
				(payload: boolean) => {
					setCanRedo(payload);
					return false;
				},
				LowPriority
			)
		);
	}, [editor, updateToolbar]);

	const codeLanguges = useMemo(() => getCodeLanguages(), []);
	const onCodeLanguageSelect = useCallback(
		(e: React.ChangeEvent<HTMLSelectElement>) => {
			e.preventDefault();
			e.stopPropagation();
			editor.update(() => {
				if (selectedElementKey !== null) {
					const node = $getNodeByKey(selectedElementKey);
					if ($isCodeNode(node)) {
						// Set the language and update the UI immediately
						node.setLanguage(e.target.value);
						setCodeLanguage(e.target.value);
						console.log("Code language set to:", e.target.value);
					}
				}
			});
		},
		[editor, selectedElementKey]
	);

	const insertLink = useCallback(() => {
		if (!isLink) {
			editor.dispatchCommand(TOGGLE_LINK_COMMAND, "https://");
			// Set isLink to true immediately for UI feedback
			setIsLink(true);
			console.log("Link inserted");
		} else {
			editor.dispatchCommand(TOGGLE_LINK_COMMAND, null);
			// Set isLink to false immediately for UI feedback
			setIsLink(false);
			console.log("Link removed");
		}
	}, [editor, isLink]);

	return (
		<div 
			className="toolbar" 
			ref={toolbarRef}
			style={{
				display: "flex",
				alignItems: "center",
				padding: "8px",
				borderBottom: "1px solid #ccc",
				backgroundColor: "#f5f5f5",
				position: "sticky",
				top: 0,
				zIndex: 100,
				flexWrap: "wrap"
			}}
		>
			<button
				disabled={!canUndo}
				onClick={(e) => {
					e.preventDefault();
					e.stopPropagation();
					console.log("Undo triggered");
					editor.dispatchCommand(UNDO_COMMAND, undefined);
				}}
				className="toolbar-item spaced"
				aria-label="Undo"
				style={{
					opacity: canUndo ? 1 : 0.5,
					cursor: canUndo ? "pointer" : "not-allowed",
					border: "none",
					background: "transparent",
					borderRadius: "4px",
					padding: "4px 8px",
					margin: "0 2px"
				}}
			>
				<i className="format undo" />
			</button>
			<button
				disabled={!canRedo}
				onClick={(e) => {
					e.preventDefault();
					e.stopPropagation();
					console.log("Redo triggered");
					editor.dispatchCommand(REDO_COMMAND, undefined);
				}}
				className="toolbar-item"
				aria-label="Redo"
				style={{
					opacity: canRedo ? 1 : 0.5,
					cursor: canRedo ? "pointer" : "not-allowed",
					border: "none",
					background: "transparent",
					borderRadius: "4px",
					padding: "4px 8px",
					margin: "0 2px"
				}}
			>
				<i className="format redo" />
			</button>
			<Divider />
			{/* Removed conditional rendering so text selector always shows */}
			<>
				<button
					className="toolbar-item block-controls"
					onMouseDown={(e) => {
						e.preventDefault();
						e.stopPropagation();
						console.log("Format dropdown toggled");
						setShowBlockOptionsDropDown(!showBlockOptionsDropDown);
					}}
					aria-label="Formatting Options"
					style={{
						display: "flex",
						alignItems: "center",
						padding: "4px 8px",
						border: "1px solid #ccc",
						borderRadius: "4px",
						backgroundColor: showBlockOptionsDropDown ? "#e0e0e0" : "#fff",
						cursor: "pointer",
						margin: "0 4px"
					}}
				>
					<span className={"icon block-type " + blockType} />
					<span className="text" style={{ marginLeft: "4px", marginRight: "4px", minWidth: "80px" }}>
						{blockTypeToBlockName[blockType as BlockType] || "Normal"}
					</span>
					<i className="chevron-down" style={{ marginLeft: "auto" }} />
				</button>
				{/* Fixed the dropdown portal rendering */}
				{showBlockOptionsDropDown && document.body && 
				createPortal(
					<BlockOptionsDropdownList
						editor={editor}
						blockType={blockType}
						toolbarRef={toolbarRef}
						setShowBlockOptionsDropDown={setShowBlockOptionsDropDown}
						setBlockType={setBlockType} // Pass setBlockType as a prop
					/>,
					document.body
				)}
				<Divider />
			</>
			{blockType === "code" ? (
				<>
					<Select
						className="toolbar-item code-language"
						onChange={onCodeLanguageSelect}
						options={codeLanguges}
						value={codeLanguage}
					/>
					<i className="chevron-down inside" />
				</>
			) : (
				<>
					<button
						onMouseDown={(e) => {
							e.preventDefault();
							e.stopPropagation();
							console.log("Format bold toggled");
							editor.dispatchCommand(FORMAT_TEXT_COMMAND, "bold");
							// Update state immediately for UI feedback
							setIsBold(!isBold);
						}}
						className={"toolbar-item spaced " + (isBold ? "active" : "")}
						aria-label="Format Bold"
						style={{
							cursor: "pointer",
							background: isBold ? "#e0e0e0" : "transparent",
							border: "none",
							borderRadius: "4px",
							padding: "4px 8px",
							margin: "0 2px"
						}}
					>
						<i className="format bold" />
					</button>
					<button
						onMouseDown={(e) => {
							e.preventDefault();
							e.stopPropagation();
							console.log("Format italic toggled");
							editor.dispatchCommand(FORMAT_TEXT_COMMAND, "italic");
							// Update state immediately for UI feedback
							setIsItalic(!isItalic);
						}}
						className={"toolbar-item spaced " + (isItalic ? "active" : "")}
						aria-label="Format Italics"
						style={{
							cursor: "pointer",
							background: isItalic ? "#e0e0e0" : "transparent",
							border: "none",
							borderRadius: "4px",
							padding: "4px 8px",
							margin: "0 2px"
						}}
					>
						<i className="format italic" />
					</button>
					<button
						onMouseDown={(e) => {
							e.preventDefault();
							e.stopPropagation();
							console.log("Format underline toggled");
							editor.dispatchCommand(FORMAT_TEXT_COMMAND, "underline");
							// Update state immediately for UI feedback
							setIsUnderline(!isUnderline);
						}}
						className={"toolbar-item spaced " + (isUnderline ? "active" : "")}
						aria-label="Format Underline"
						style={{
							cursor: "pointer",
							background: isUnderline ? "#e0e0e0" : "transparent",
							border: "none",
							borderRadius: "4px",
							padding: "4px 8px",
							margin: "0 2px"
						}}
					>
						<i className="format underline" />
					</button>
					<button
						onMouseDown={(e) => {
							e.preventDefault();
							e.stopPropagation();
							console.log("Format strikethrough toggled");
							editor.dispatchCommand(FORMAT_TEXT_COMMAND, "strikethrough");
							// Update state immediately for UI feedback
							setIsStrikethrough(!isStrikethrough);
						}}
						className={
							"toolbar-item spaced " + (isStrikethrough ? "active" : "")
						}
						aria-label="Format Strikethrough"
						style={{
							cursor: "pointer",
							background: isStrikethrough ? "#e0e0e0" : "transparent",
							border: "none",
							borderRadius: "4px",
							padding: "4px 8px",
							margin: "0 2px"
						}}
					>
						<i className="format strikethrough" />
					</button>
					<button
						onMouseDown={(e) => {
							e.preventDefault();
							e.stopPropagation();
							console.log("Format code toggled");
							editor.dispatchCommand(FORMAT_TEXT_COMMAND, "code");
							// Update state immediately for UI feedback
							setIsCode(!isCode);
						}}
						className={"toolbar-item spaced " + (isCode ? "active" : "")}
						aria-label="Insert Code"
						style={{
							cursor: "pointer",
							background: isCode ? "#e0e0e0" : "transparent",
							border: "none",
							borderRadius: "4px",
							padding: "4px 8px",
							margin: "0 2px"
						}}
					>
						<i className="format code" />
					</button>
					<button
						onMouseDown={(e) => {
							e.preventDefault();
							e.stopPropagation();
							console.log("Link toggled");
							insertLink();
						}}
						className={"toolbar-item spaced " + (isLink ? "active" : "")}
						aria-label="Insert Link"
						style={{
							cursor: "pointer",
							background: isLink ? "#e0e0e0" : "transparent",
							border: "none",
							borderRadius: "4px",
							padding: "4px 8px",
							margin: "0 2px"
						}}
					>
						<i className="format link" />
					</button>
					{isLink &&
					createPortal(<FloatingLinkEditor editor={editor} />, document.body)}
					<Divider />
					<button
						onMouseDown={(e) => {
							e.preventDefault();
							e.stopPropagation();
							console.log("Left align applied");
							editor.dispatchCommand(FORMAT_ELEMENT_COMMAND, "left");
						}}
						className="toolbar-item spaced"
						aria-label="Left Align"
						style={{
							cursor: "pointer",
							border: "none",
							borderRadius: "4px",
							padding: "4px 8px",
							margin: "0 2px"
						}}
					>
						<i className="format left-align" />
					</button>
					<button
						onMouseDown={(e) => {
							e.preventDefault();
							e.stopPropagation();
							console.log("Center align applied");
							editor.dispatchCommand(FORMAT_ELEMENT_COMMAND, "center");
						}}
						className="toolbar-item spaced"
						aria-label="Center Align"
						style={{
							cursor: "pointer",
							border: "none",
							borderRadius: "4px",
							padding: "4px 8px",
							margin: "0 2px"
						}}
					>
						<i className="format center-align" />
					</button>
					<button
						onMouseDown={(e) => {
							e.preventDefault();
							e.stopPropagation();
							console.log("Right align applied");
							editor.dispatchCommand(FORMAT_ELEMENT_COMMAND, "right");
						}}
						className="toolbar-item spaced"
						aria-label="Right Align"
						style={{
							cursor: "pointer",
							border: "none",
							borderRadius: "4px",
							padding: "4px 8px",
							margin: "0 2px"
						}}
					>
						<i className="format right-align" />
					</button>
					<button
						onMouseDown={(e) => {
							e.preventDefault();
							e.stopPropagation();
							console.log("Justify align applied");
							editor.dispatchCommand(FORMAT_ELEMENT_COMMAND, "justify");
						}}
						className="toolbar-item"
						aria-label="Justify Align"
						style={{
							cursor: "pointer",
							border: "none",
							borderRadius: "4px",
							padding: "4px 8px",
							margin: "0 2px"
						}}
					>
						<i className="format justify-align" />
					</button>
				</>
			)}
		</div>
	);
}